import React from 'react';

/**
 * Error boundary that catches rendering errors in its subtree and shows a
 * user-friendly fallback UI instead of an empty/black screen.
 *
 * Use sparingly at the root of pages or around well-known risky subtrees.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Caught error:', error, info);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    // Force a re-render of the route by reloading just the current location.
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  handleHome = () => {
    if (typeof window !== 'undefined') {
      window.location.assign('/home');
    }
  };

  render() {
    if (this.state.hasError) {
      const { fallback } = this.props;
      if (fallback) return fallback;
      const message = this.state.error?.message || 'Erro inesperado';
      return (
        <div
          className="min-h-screen bg-background flex items-center justify-center px-4"
          data-testid="error-boundary-fallback"
        >
          <div className="max-w-md w-full text-center">
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-ruby/15 border border-ruby/30 flex items-center justify-center text-3xl">
              ⚠
            </div>
            <h1 className="font-brand text-2xl font-bold text-foreground mb-2">
              Algo correu mal ao mostrar esta página
            </h1>
            <p className="text-muted-foreground text-sm mb-6">
              Já registámos o erro. Podes tentar recarregar ou voltar ao início.
            </p>
            <pre className="text-left text-xs text-muted-foreground bg-surface border border-border rounded-lg p-3 mb-6 overflow-x-auto">
              {message}
            </pre>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <button
                onClick={this.handleReload}
                className="px-5 py-2.5 rounded-full bg-ruby hover:bg-ruby-light text-white text-sm font-semibold transition-colors"
                data-testid="error-boundary-reload"
              >
                Recarregar
              </button>
              <button
                onClick={this.handleHome}
                className="px-5 py-2.5 rounded-full bg-surface border border-border hover:border-ruby/40 text-foreground text-sm font-semibold transition-colors"
                data-testid="error-boundary-home"
              >
                Voltar ao início
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
